import { Banner, Button, Checkbox, LegacyCard, LegacyStack, TextField } from '@shopify/polaris'
import { useFormikContext } from 'formik'
import { useEffect, useState } from 'react'
import useSWR, { mutate } from 'swr'

import { AdaptiveModal } from '../common/AdaptiveModal'
import { backendFetchResultData, Result } from '../common/api'
import { ApiAction, useApiActionState } from '../common/ApiAction'
import {
  CenteredSpinner,
  DialogActivator,
  useDialogActivator,
  useFormikHandleChangeCallback,
} from '../common/helpers'
import { UserSettings } from './api'

interface IpAccess {
  allowedIps: string[]
}

const swrKey = ['merchant-api-allowed-ips']

const useAllowedIps = () =>
  useSWR<string[]>(swrKey, () =>
    backendFetchResultData<IpAccess>('GET', '/merchant-api-ip-access').then((d) => d.allowedIps)
  )

const AllowedIpsModal = ({ activator }: { activator: DialogActivator }) => {
  const { data: initialIps, isValidating: loading } = useAllowedIps()
  const [draft, setDraft] = useState('')

  useEffect(() => {
    setDraft(initialIps?.join('\n') ?? '')
  }, [initialIps, activator.showCounter])

  const saveAction = useApiActionState({
    method: 'PUT',
    url: '/merchant-api-ip-access',
    opts: { body: JSON.stringify({ allowedIps: draft }) },
    onSuccess: (res: Result<IpAccess>) => {
      if (res.data) {
        mutate(swrKey, res.data.allowedIps, false)
        activator.close()
      }
    },
  })

  const { dismissMessage } = saveAction
  useEffect(() => {
    dismissMessage()
  }, [dismissMessage, activator.showCounter])

  const unchanged = draft === (initialIps?.join('\n') ?? '')

  return (
    <>
      <AdaptiveModal
        title="Allowed IP addresses"
        open={activator.open}
        onClose={activator.close}
        sectioned
        primaryAction={{
          content: 'Save',
          onAction: saveAction.run,
          loading: saveAction.loading,
          disabled: initialIps === undefined || unchanged,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: activator.close }]}
      >
        {loading && initialIps === undefined && <CenteredSpinner />}
        {initialIps !== undefined && (
          <TextField
            label="Allowed IP addresses"
            labelHidden
            value={draft}
            onChange={setDraft}
            error={saveAction.isError ? saveAction.message : undefined}
            multiline={8}
            autoComplete="off"
            autoFocus
            monospaced
            spellCheck={false}
            disabled={saveAction.loading}
            placeholder={'192.0.2.1\n2001:db8::1'}
            helpText="Enter one IPv4 or IPv6 address per line (up to 100). Use the public outgoing IPs of your integrations. Ranges and wildcards are not supported. Changes apply as soon as they are saved."
          />
        )}
      </AdaptiveModal>
      <ApiAction state={saveAction} />
    </>
  )
}

export const MerchantApiIpAccessSection = () => {
  const formik = useFormikContext<UserSettings>()
  const handleChange = useFormikHandleChangeCallback(formik.setFieldValue)
  const { data: allowedIps, error } = useAllowedIps()
  const activator = useDialogActivator()

  const enabled = formik.values.merchant_api_ip_allowlist_enabled
  const listEmpty = allowedIps !== undefined && allowedIps.length === 0

  return (
    <LegacyCard.Section>
      <LegacyStack vertical>
        {error && (
          <Banner tone="critical">
            Unable to load the allowed IP addresses. Please reload and try again.
          </Banner>
        )}
        <Checkbox
          id="merchant_api_ip_allowlist_enabled"
          name="merchant_api_ip_allowlist_enabled"
          label="Restrict API requests to the allowed IP addresses"
          checked={enabled}
          onChange={handleChange}
          disabled={listEmpty && !enabled}
        />
        <Button onClick={activator.show}>
          {allowedIps
            ? `Manage allowed IP addresses (${allowedIps.length})`
            : 'Manage allowed IP addresses'}
        </Button>
      </LegacyStack>
      <AllowedIpsModal activator={activator} />
    </LegacyCard.Section>
  )
}
